import type { Transaction } from '@fd/schemas';
import { buildFlowGraph, periodContaining } from '@fd/finance-core';
import { ArrowDown, ArrowRight, ArrowUp, Check, EyeOff, GripVertical, LayoutGrid, Plus, Receipt, RotateCcw, Scale } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  groupOf,
  today,
  useBudgets,
  useCategories,
  useCategoryGroups,
  useDashboard,
  useEntities,
  useHousehold,
  useSettlementContext,
  type DashboardData,
} from '../app/data.ts';
import { buildBudgetPlan, flowBudget } from '../app/budget-plan.ts';
import { useSession } from '../app/session.tsx';
import { settlementIn, storedSettlementScope } from '../app/settlement-scope.ts';
import { readPreference, writePreference } from '../app/config.ts';
import { useT } from '../app/i18n.tsx';
import { useMediaQuery } from '../app/media.ts';
import { hrefFor, navigate } from '../app/router.tsx';
import { usePeriod } from '../shell/Shell.tsx';
import { wholeGroup } from '../ui/CategoryOptions.tsx';
import { FlowDiagram } from '../ui/FlowDiagram.tsx';
import { Avatar, Card, EmptyState, Money, Skeleton } from '../ui/primitives.tsx';
import { TransactionRow } from '../ui/TransactionRow.tsx';
import { BankBanners } from './Banks.tsx';
import { DashboardCurrencyNotice, DashboardLoadError } from './DashboardNotice.tsx';
import { DashboardBoard } from './DashboardBoard.tsx';
import { DEFAULT_GRID, parseGrid, setTileHidden, storedGrid, type Grid } from './dashboard-grid.ts';
import { defaultLayout, moveWidget, parseLayout, setHidden, storedLayout, type DashboardLayout, type WidgetId } from './dashboard-layout.ts';
import { layoutSource } from './dashboard-sync.ts';
import { Kpis } from './overview/Kpis.tsx';
import { Snapshot } from './overview/Snapshot.tsx';
import { SpendingCard } from './overview/SpendingCard.tsx';
import { SavingsCard } from './SavingsCard.tsx';
import { SpendingHeatmap } from './SpendingHeatmap.tsx';
import { UpcomingPayments } from './UpcomingPayments.tsx';

/**
 * Who settles with whom: the amount leads, the direction reads as a sentence. Counted as on
 * Settlements, in the view last chosen there — the period above, or everything not settled.
 */
function SettlementCard({ data }: { data: DashboardData }) {
  const t = useT();
  const context = useSettlementContext();
  const period = usePeriod();
  const current = periodContaining(period.kind, period.anchor);
  const scope = storedSettlementScope();
  const settlement = useMemo(
    () => (context ? settlementIn(context, scope, current) : data.settlement),
    [context, scope, current.start, current.end, data.settlement],
  );
  const periodLabel =
    period.kind === 'MONTH'
      ? t.date(current.start, 'monthYear')
      : period.kind === 'YEAR'
        ? current.start.slice(0, 4)
        : `${t.date(current.start, 'dayMonth')} – ${t.date(current.end, 'dayMonth')}`;
  const ledger =
    settlement.ledgers.find((l) => l.currency === data.household.baseCurrency) ??
    settlement.ledgers[0];
  const person = (id: string) => data.persons.find((p) => p.id === id);
  const name = (id: string) => person(id)?.displayName ?? '?';
  const transfers = ledger?.suggestedTransfers ?? [];
  // Shared costs only: what each paid for them and their share. Personal spending paid by the
  // person it was for moves nothing and stays out (the owner saw £54k "shared" of all his spending).
  const shared = useMemo(() => {
    const items = settlement.items.filter(
      (i) =>
        i.currency === ledger?.currency &&
        i.expenseType !== 'PERSONAL' &&
        i.expenseType !== 'EXCLUDED',
    );
    const sums = new Map<string, { paid: bigint; owed: bigint }>();
    for (const item of items) {
      for (const p of item.paid)
        sums.set(p.personId, {
          paid: (sums.get(p.personId)?.paid ?? 0n) + p.amountMinor,
          owed: sums.get(p.personId)?.owed ?? 0n,
        });
      for (const o of item.owed)
        sums.set(o.personId, {
          paid: sums.get(o.personId)?.paid ?? 0n,
          owed: (sums.get(o.personId)?.owed ?? 0n) + o.amountMinor,
        });
    }
    const since = items.reduce<string | null>(
      (min, i) => (min === null || i.bookingDate < min ? i.bookingDate : min),
      null,
    );
    return { sums, since };
  }, [settlement.items, ledger?.currency]);
  // Everyone who shared a cost, the largest bar setting the scale.
  const people = (ledger?.persons ?? [])
    .map((p) => ({
      ...p,
      paidMinor: shared.sums.get(p.personId)?.paid ?? 0n,
      owedMinor: shared.sums.get(p.personId)?.owed ?? 0n,
    }))
    .filter((p) => p.paidMinor > 0n || p.owedMinor > 0n);
  const scale = Math.max(
    1,
    ...people.map((p) => Math.max(Number(p.paidMinor), Number(p.owedMinor))),
  );
  return (
    <Card
      title={t.t('dashboard.settlement')}
      subtitle={
        scope === 'period'
          ? t.t('settlement.scopePeriod', { period: periodLabel })
          : shared.since
          ? [
              t.t('settlement.since', { date: t.date(shared.since) }),
              ledger?.persons.every((p) => p.settledMinor === 0n)
                ? t.t('settlement.neverSettled')
                : null,
            ]
              .filter(Boolean)
              .join(' · ')
          : undefined
      }
      action={
        <a className="card__link" href={hrefFor({ page: 'settlements' })}>
          {t.t('dashboard.viewAll')} <ArrowRight aria-hidden="true" />
        </a>
      }
    >
      {!ledger || transfers.length === 0 ? (
        <div className="settle-mini settle-mini--even">
          <span className="settle-mini__icon" aria-hidden="true">
            <Scale size={18} />
          </span>
          <div className="stack" style={{ gap: 2 }}>
            <strong>{t.t('settlement.allSettled')}</strong>
            <span className="list-row__meta">{t.t('settlement.allSettledHint')}</span>
          </div>
        </div>
      ) : (
        <div className="stack gap-3">
          {transfers.map((transfer) => (
            <a
              key={`${transfer.fromPersonId}-${transfer.toPersonId}`}
              className="settle-mini"
              href={hrefFor({ page: 'settlements' })}
            >
              <span className="settle-mini__people" aria-hidden="true">
                <Avatar
                  name={name(transfer.fromPersonId)}
                  color={person(transfer.fromPersonId)?.color ?? null}
                />
                <ArrowRight size={14} />
                <Avatar
                  name={name(transfer.toPersonId)}
                  color={person(transfer.toPersonId)?.color ?? null}
                />
              </span>
              <span className="stack grow" style={{ gap: 2, minWidth: 0 }}>
                <span className="truncate" style={{ fontWeight: 560 }}>
                  {t.t('settlement.transfer', {
                    from: name(transfer.fromPersonId),
                    to: name(transfer.toPersonId),
                  })}
                </span>
                {settlement.issues.length > 0 && (
                  <span className="list-row__meta fg-warning">
                    {t.plural(
                      'settlement.needsReview',
                      new Set(settlement.issues.map((i) => i.transactionId)).size,
                    )}
                  </span>
                )}
              </span>
              <strong className="settle-mini__amount">
                <Money minor={transfer.amountMinor} currency={ledger.currency} animate />
              </strong>
            </a>
          ))}
        </div>
      )}
      {people.length > 1 && (
        <div className="settle-mini-people">
          <span className="overline">{t.t('settlement.sharedSoFar')}</span>
          {people.map((p) => {
            const who = person(p.personId);
            const color = who?.color ?? 'var(--fill-neutral)';
            return (
              <div key={p.personId} className="settle-mini-person">
                <Avatar name={name(p.personId)} color={who?.color ?? null} />
                <div className="stack grow" style={{ gap: 4, minWidth: 0 }}>
                  <div className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
                    <span className="truncate" style={{ fontWeight: 560 }}>
                      {name(p.personId)}
                    </span>
                    <span
                      className={`settle-mini-person__net ${p.netMinor > 0n ? 'fg-income' : p.netMinor < 0n ? 'fg-expense' : 'text-muted'}`}
                    >
                      {t.money(
                        { minor: p.netMinor, currency: ledger!.currency },
                        { signDisplay: 'exceptZero' },
                      )}
                    </span>
                  </div>
                  {/* What they paid for shared costs, and a tick at their share of them. */}
                  <div className="settle-mini-person__bar" aria-hidden="true">
                    <span
                      className="settle-mini-person__paid"
                      style={{
                        width: `${(Number(p.paidMinor) / scale) * 100}%`,
                        background: color,
                      }}
                    />
                    <span
                      className="settle-mini-person__share"
                      style={{ left: `${(Number(p.owedMinor) / scale) * 100}%` }}
                    />
                  </div>
                  <span className="list-row__meta amount-text">
                    {t.t('settlement.paidVsShare', {
                      paid: t.money({ minor: p.paidMinor, currency: ledger!.currency }),
                      share: t.money({ minor: p.owedMinor, currency: ledger!.currency }),
                    })}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <a className="btn btn--secondary btn--sm settle-cta" href={hrefFor({ page: 'settlements' })}>
        <Scale aria-hidden="true" />
        {t.t(transfers.length > 0 ? 'settlement.settleNow' : 'settlement.open')}
      </a>
    </Card>
  );
}

function DashboardSkeleton() {
  return (
    <div className="dash" aria-busy="true">
      <section className="card snapshot col-12">
        <div className="snapshot__main">
          <Skeleton width={110} height={12} />
          <Skeleton width={240} height={40} />
          <Skeleton width={180} height={14} />
          <Skeleton height={150} radius={10} />
        </div>
        <div className="snapshot__figures">
          <Skeleton height={48} />
          <Skeleton height={48} />
          <Skeleton height={48} />
        </div>
      </section>
      <div className="metrics col-12">
        {[0, 1, 2, 3].map((key) => (
          <div className="metric" key={key}>
            <Skeleton width={90} height={14} />
            <Skeleton width={140} height={28} />
            <Skeleton height={14} />
          </div>
        ))}
      </div>
      <section className="card col-8" style={{ height: 360 }} />
      <section className="card col-4" style={{ height: 360 }} />
    </div>
  );
}

/** The order of cards on a narrow screen (and the wide screen's before the board, carried over from it). */
const LAYOUT_KEY = 'overviewLayout';
/** The board of cards on a wide screen, on a grid of cells (dashboard-grid.ts). */
const BOARD_KEY = 'overviewCells';
/** The first board, of 8 px rows (29.09.2026), carried over to the cells. */
const FIRST_BOARD_KEY = 'overviewBoard';
/** Set once this device takes the household's layout (dashboard-sync.ts). */
const SHARED_KEY = 'overviewShared';

export function DashboardPage({ onQuickAdd }: { onQuickAdd(): void }) {
  const t = useT();
  const period = usePeriod();
  const { data, loading, error, retry, retrying } = useDashboard(period.kind, period.anchor);
  // Below 1100 px every card takes the whole width, one under another in an order; wider, the cards
  // sit on the board. A phone starts from its own order (what matters most first).
  const stacked = useMediaQuery('(max-width: 1099px)');
  const [narrow] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches);
  const [layout, setLayout] = useState(() => parseLayout(readPreference(LAYOUT_KEY), narrow));
  const [grid, setGrid] = useState(() => parseGrid(readPreference(BOARD_KEY), readPreference(FIRST_BOARD_KEY), narrow ? null : readPreference(LAYOUT_KEY)));
  const [editing, setEditing] = useState(false);
  // The same cards on every device: the household carries the layout; a backend that does not know
  // the fields yet leaves each device its own (dashboard-sync.ts).
  const { engine, householdId, requestSync } = useSession();
  const household = useHousehold();
  const shared = household !== null && Object.hasOwn(household, 'dashboardBoard');
  const share = (patch: { dashboardBoard?: string | null; dashboardList?: string | null }) => {
    if (!shared || !householdId) return;
    writePreference(SHARED_KEY, '1');
    void engine.update(householdId, 'household', householdId, patch).then(requestSync, () => undefined);
  };
  const saveLayout = (next: DashboardLayout) => {
    setLayout(next);
    writePreference(LAYOUT_KEY, storedLayout(next, narrow));
    share({ dashboardList: storedLayout(next, narrow) });
  };
  const saveGrid = (next: Grid) => {
    setGrid(next);
    writePreference(BOARD_KEY, storedGrid(next));
    share({ dashboardBoard: storedGrid(next) });
  };
  // Another device's change arrives with the household; not in the middle of editing here.
  const remoteBoard = shared ? (household.dashboardBoard ?? null) : undefined;
  const remoteList = shared ? (household.dashboardList ?? null) : undefined;
  useEffect(() => {
    if (editing || remoteBoard === undefined) return;
    const adopted = readPreference(SHARED_KEY) === '1';
    const board = layoutSource({ local: storedGrid(grid), remote: remoteBoard, adopted });
    const list = layoutSource({ local: storedLayout(layout, narrow), remote: remoteList, adopted });
    if (board.send || list.send) share({ ...(board.send ? { dashboardBoard: board.show } : {}), ...(list.send ? { dashboardList: list.show } : {}) });
    else writePreference(SHARED_KEY, '1');
    if (board.show !== storedGrid(grid)) {
      setGrid(parseGrid(board.show));
      writePreference(BOARD_KEY, board.show);
    }
    if (list.show !== storedLayout(layout, narrow)) {
      setLayout(parseLayout(list.show, narrow));
      writePreference(LAYOUT_KEY, list.show);
    }
    // Only the household's layout and the end of editing start this; the device's own state is read.
  }, [remoteBoard, remoteList, editing]);
  const [over, setOver] = useState<WidgetId | null>(null);
  const dragged = useRef<WidgetId | null>(null);
  const transactions = useEntities<Transaction>('transaction');
  // The bars view is gone; an old choice of it opens the table.
  const groups = useCategoryGroups();
  // The month's budget lives in the flow now: each category's limit on its tile, the total under it.
  const budgetData = useBudgets(period.anchor);
  const allCategories = useCategories();
  const budget = useMemo(() => {
    if (!budgetData) return null;
    const now = today();
    return flowBudget(buildBudgetPlan(budgetData, allCategories, groups, now), budgetData, now);
  }, [budgetData, allCategories, groups]);
  // Every category on its own (the owner's wish), nothing folded into "other"; a group of
  // categories (D-035: "Auto", "Rachunki") drawn once.
  const graph = useMemo(() => {
    if (!data) return null;
    const byId = new Map(data.categories.map((c) => [c.id, c]));
    // Whose pay it is: the one owner of the account it came into (a joint account says nobody's).
    const owners = new Map(
      data.accounts.map((a) => [a.id, a.owners.length === 1 ? a.owners[0]! : null]),
    );
    return buildFlowGraph(data.flow, {
      earnerOf: (accountId) => owners.get(accountId) ?? null,
      maxExpenseTargets: Number.POSITIVE_INFINITY,
      maxIncomeSources: Number.POSITIVE_INFINITY,
      groupOf: (id) => (id ? (groupOf(byId.get(id), groups)?.id ?? null) : null),
    });
  }, [data, groups]);

  if (!data && error) return <DashboardLoadError onRetry={retry} retrying={retrying} />;
  if (loading || !data) return <DashboardSkeleton />;

  const currency = data.household.baseCurrency;
  // The cash flow's own totals: with payments still pending at the bank, as its diagram.
  const booked = data.flow.booked;
  const hasAnyTransaction = (transactions.data?.length ?? 0) > 0;
  const body: Record<WidgetId, ReactNode> = {
    overview: <Snapshot data={data} />,
    metrics: <Kpis data={data} />,
    flow: (
        <Card
          className="flow-card"
          title={t.t('dashboard.cashFlow')}
          subtitle={
            <span title={t.t('dashboard.netFlowHint')}>
              {t.t('dashboard.netFlow')}:{' '}
              <Money
                minor={booked.netMinor}
                currency={currency}
                options={{ signDisplay: 'exceptZero' }}
              />
            </span>
          }
          action={
            <div className="flow-card__actions">
              <a className="card__link" href={hrefFor({ page: 'transactions', view: 'reports' })}>
                {t.t('studio.openReports')} <ArrowRight aria-hidden="true" />
              </a>
            </div>
          }
        >
          {hasAnyTransaction && graph ? (
            <FlowDiagram
              graph={graph}
              categories={data.categories}
              currency={currency}
              incomeMinor={booked.incomeMinor}
              groups={groups}
              budget={budget}
              persons={data.persons}
              accounts={data.accounts}
              onOpen={(node) =>
                navigate({
                  page: 'transactions',
                  categoryId: node.groupId ? wholeGroup(node.groupId) : (node.categoryId ?? 'none'),
                })
              }
            />
          ) : (
            <EmptyState
              icon={<Receipt size={20} />}
              title={t.t('transactions.empty')}
              text={t.t('transactions.emptyHint')}
              actions={
                <button className="btn btn--primary" type="button" onClick={() => onQuickAdd()}>
                  {t.t('transactions.addManually')}
                </button>
              }
            />
          )}
        </Card>
    ),
    settlement: (
        <div className="settlement-slot">
          <SettlementCard data={data} />
        </div>
    ),
    recent: (
        <Card
          className="recent-card"
          title={t.t('dashboard.recentTransactions')}
          flush
          action={
            <a className="card__link" href={hrefFor({ page: 'transactions' })}>
              {t.t('dashboard.viewAll')} <ArrowRight aria-hidden="true" />
            </a>
          }
        >
          {data.recent.slice(0, 5).map((transaction) => (
            <TransactionRow
              key={transaction.id}
              transaction={transaction}
              category={data.categories.find((c) => c.id === transaction.categoryId)}
              account={data.accounts.find((a) => a.id === transaction.accountId)}
              accounts={data.accounts}
              persons={data.persons}
              onOpen={() => navigate({ page: 'transactions', id: transaction.id })}
            />
          ))}
          {!data.recent.length && (
            <EmptyState icon={<Receipt size={20} />} title={t.t('transactions.empty')} />
          )}
        </Card>
    ),
    upcoming: (
        <div className="dash-stack">
          <UpcomingPayments />
        </div>
    ),
    heatmap: <SpendingHeatmap currency={currency} />,
    changes: <SpendingCard data={data} />,
    savings: <SavingsCard data={data} className="savings-card" />,
  };
  const shown = layout.order.filter((id) => !layout.hidden.includes(id));
  const hidden = stacked ? layout.hidden : grid.hidden;
  const show = (id: WidgetId) => (stacked ? saveLayout(setHidden(layout, id, false)) : saveGrid(setTileHidden(grid, id, false)));

  return (
    <div className="dash">
      {/* Accounts the bank offers without a decision yet, or a connection to renew: seen from here too. */}
      <div className="col-12 dash-banners">
        <BankBanners onChoose={(c) => navigate({ page: 'accounts', bankConnection: c.id })} />
      </div>
      {error && <DashboardLoadError onRetry={retry} retrying={retrying} />}
      <DashboardCurrencyNotice data={data} transactions={transactions.data ?? []} />
      {/* The cards where the owner put them (the same on every device); in editing they are moved and sized. */}
      <div className="col-12 dash-tools">
        {editing ? (
          <>
            <span className="text-secondary grow">{t.t(stacked ? 'dashboard.layout.hint' : 'dashboard.layout.hintBoard')}</span>
            <button className="btn btn--ghost btn--sm" type="button" onClick={() => (stacked ? saveLayout(defaultLayout(narrow)) : saveGrid(DEFAULT_GRID))}>
              <RotateCcw aria-hidden="true" />
              {t.t('dashboard.layout.reset')}
            </button>
            <button className="btn btn--primary btn--sm" type="button" onClick={() => setEditing(false)}>
              <Check aria-hidden="true" />
              {t.t('dashboard.layout.done')}
            </button>
          </>
        ) : (
          <button className="btn btn--ghost btn--sm" type="button" onClick={() => setEditing(true)}>
            <LayoutGrid aria-hidden="true" />
            {t.t('dashboard.layout.edit')}
          </button>
        )}
      </div>
      {!stacked && (
        <div className="col-12">
          <DashboardBoard grid={grid} editing={editing} body={body} onChange={saveGrid} />
        </div>
      )}
      {stacked && shown.map((id, index) => (
        <div
          key={id}
          className={`dash-slot col-12${editing ? ' dash-slot--edit' : ''}${over === id ? ' dash-slot--over' : ''}`}
          draggable={editing}
          onDragStart={(e) => {
            dragged.current = id;
            e.dataTransfer.effectAllowed = 'move';
          }}
          onDragOver={(e) => {
            if (!editing || !dragged.current) return;
            e.preventDefault();
            setOver(id);
          }}
          onDragLeave={() => setOver((current) => (current === id ? null : current))}
          onDrop={(e) => {
            e.preventDefault();
            if (dragged.current && dragged.current !== id) saveLayout(moveWidget(layout, dragged.current, layout.order.indexOf(id)));
            dragged.current = null;
            setOver(null);
          }}
          onDragEnd={() => {
            dragged.current = null;
            setOver(null);
          }}
        >
          {editing && (
            <div className="dash-slot__bar">
              <GripVertical className="dash-slot__grip" aria-hidden="true" />
              <strong className="grow truncate">{t.dynamic(`dashboard.widgets.${id}`)}</strong>
              <button className="icon-btn" type="button" disabled={index === 0} aria-label={`${t.t('dashboard.layout.up')}: ${t.dynamic(`dashboard.widgets.${id}`)}`} title={t.t('dashboard.layout.up')} onClick={() => saveLayout(moveWidget(layout, id, layout.order.indexOf(shown[index - 1]!)))}>
                <ArrowUp />
              </button>
              <button className="icon-btn" type="button" disabled={index === shown.length - 1} aria-label={`${t.t('dashboard.layout.down')}: ${t.dynamic(`dashboard.widgets.${id}`)}`} title={t.t('dashboard.layout.down')} onClick={() => saveLayout(moveWidget(layout, id, layout.order.indexOf(shown[index + 1]!)))}>
                <ArrowDown />
              </button>
              <button className="icon-btn" type="button" aria-label={`${t.t('dashboard.layout.hide')}: ${t.dynamic(`dashboard.widgets.${id}`)}`} title={t.t('dashboard.layout.hide')} onClick={() => saveLayout(setHidden(layout, id, true))}>
                <EyeOff />
              </button>
            </div>
          )}
          {/* The cash flow grows with its tiles (FlowDiagram), so
              nothing in it scrolls (the owner, 30.09.2026: "it is to be without scrolling"). */}
          {body[id]}
        </div>
      ))}
      {editing && hidden.length > 0 && (
        <div className="col-12 dash-hidden">
          <span className="text-secondary">{t.t('dashboard.layout.hidden')}</span>
          {hidden.map((id) => (
            <button key={id} className="btn btn--secondary btn--sm" type="button" onClick={() => show(id)}>
              <Plus aria-hidden="true" />
              {t.dynamic(`dashboard.widgets.${id}`)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
